import React from 'react';
import './Pages.css';

const About: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>About GET AI PORTRAIT</h1>
        <p className="page-subtitle">Democratizing Professional Portrait Photography Through AI</p>
      </div>

      <div className="page-content">
        <section className="content-section">
          <h2>Our Mission</h2>
          <p>
            <strong>GET AI PORTRAIT</strong> is revolutionizing personal photography by making professional-quality
            portraits accessible to everyone. Using cutting-edge artificial intelligence, we transform ordinary
            selfies into stunning artistic portraits that capture your unique essence — no photographer,
            no studio, no expensive equipment required.
          </p>
          <p>
            We believe that everyone deserves beautiful portrait photography for their social media, dating
            profiles, professional headshots, or creative projects. Our mission is to democratize portrait
            photography through AI technology that delivers Hollywood-quality results in under 60 seconds.
          </p>
        </section>

        <section className="content-section">
          <h2>The Story Behind GET AI PORTRAIT</h2>
          <p>
            GET AI PORTRAIT was born from a simple frustration: professional portrait photography is expensive,
            time-consuming, and often inaccessible. As social media became the primary way we present ourselves
            online, the demand for high-quality portraits skyrocketed — but traditional photography couldn't
            keep up.
          </p>
          <p>
            In 2024, we set out to solve this problem using the latest advancements in artificial intelligence.
            After months of research, experimentation, and collaboration with AI model providers (Replicate,
            OpenAI), we launched GET AI PORTRAIT: an iOS app that brings professional portrait photography
            to your fingertips.
          </p>
        </section>

        <section className="content-section">
          <h2>Why Choose GET AI PORTRAIT?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div>
              <h3>🎨 56+ Unique Styles</h3>
              <p>From Hollywood glamour to cyberpunk aesthetics, fantasy renaissance to corporate headshots</p>
            </div>
            <div>
              <h3>⚡ Lightning Fast</h3>
              <p>Generate stunning portraits in under 60 seconds — faster than booking a photographer</p>
            </div>
            <div>
              <h3>🔒 Privacy First</h3>
              <p>GDPR & EU AI Act compliant. Biometric data processed temporarily, deleted after generation</p>
            </div>
            <div>
              <h3>✨ AI Excellence</h3>
              <p>Powered by FLUX 1.1 Pro, DALL-E 3, and Stable Diffusion — the best AI models available</p>
            </div>
            <div>
              <h3>👁️ Try Before Buy</h3>
              <p>Preview watermarked portraits before purchasing — no blind purchases</p>
            </div>
            <div>
              <h3>📱 Native iOS</h3>
              <p>Built with SwiftUI, Core ML, and CloudKit — optimized for iPhone and iPad</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>Our Technology</h2>
          <p>
            GET AI PORTRAIT leverages state-of-the-art artificial intelligence to deliver unparalleled portrait quality:
          </p>

          <h3>AI Models</h3>
          <ul>
            <li><strong>FLUX 1.1 Pro:</strong> Latest generation portrait AI with photorealistic output</li>
            <li><strong>GPT-Image-1 (DALL-E 3):</strong> Advanced artistic style transfer</li>
            <li><strong>Stable Diffusion:</strong> Open-source foundation for artistic portraits</li>
            <li><strong>Apple Vision Framework:</strong> On-device face detection (&gt; 95% accuracy)</li>
          </ul>

          <h3>Quality Assurance</h3>
          <ul>
            <li>Identity preservation threshold: 0.4-0.5 similarity score</li>
            <li>Trained on diverse datasets for all skin tones and face types</li>
            <li>Human-in-the-loop quality ratings (&gt; 4.2/5 average)</li>
            <li>Full HD output (2048x2048 pixels)</li>
          </ul>

          <h3>Privacy & Compliance</h3>
          <ul>
            <li><strong>GDPR Compliant:</strong> Explicit consent for biometric data processing</li>
            <li><strong>EU AI Act Compliant:</strong> All portraits clearly labeled as AI-generated</li>
            <li><strong>No Facial Recognition:</strong> Face detection for artistic transformation only</li>
            <li><strong>Data Deletion:</strong> Biometric data deleted immediately after generation</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>DREIF AI — The Company Behind the App</h2>
          <p>
            GET AI PORTRAIT is a product by <strong>DREIF AI</strong>, a German technology company specializing
            in AI-powered creative tools. Founded by Dominik Reif in 2024, DREIF AI is committed to making
            artificial intelligence accessible, ethical, and beneficial for everyone.
          </p>

          <h3>Our Values</h3>
          <ul>
            <li><strong>Privacy First:</strong> We never sell user data. Period.</li>
            <li><strong>Transparency:</strong> Clear labeling of AI-generated content (EU AI Act compliance)</li>
            <li><strong>Accessibility:</strong> Premium portrait photography for everyone, not just the wealthy</li>
            <li><strong>Innovation:</strong> Always using the latest AI models and techniques</li>
            <li><strong>User-Centric:</strong> Built with feedback from 1000+ beta testers</li>
          </ul>

          <h3>Portfolio</h3>
          <ul>
            <li><strong>GET AI PORTRAIT</strong> (2024): AI portrait generation for iOS</li>
            <li><strong>Mirolumo</strong> (2024): AI-powered inspiration & mindfulness app</li>
            <li><strong>InspireMe</strong> (2023): Daily motivational quotes with AI reflections</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>The Team</h2>
          <p>
            <strong>Dominik Reif</strong><br />
            Founder & Lead Developer<br />
            <em>iOS Engineer, AI Enthusiast, Privacy Advocate</em>
          </p>
          <p>
            Dominik is a full-stack developer with expertise in iOS development (Swift, SwiftUI), AI/ML
            integration, and privacy-first product design. Based in Germany, he's passionate about making
            AI technology accessible while respecting user privacy and regulatory compliance.
          </p>

          <h3>Collaborations</h3>
          <ul>
            <li><strong>Replicate:</strong> AI model hosting (FLUX 1.1 Pro)</li>
            <li><strong>OpenAI:</strong> Advanced AI models (GPT-Image-1)</li>
            <li><strong>Firebase:</strong> Cloud infrastructure and analytics</li>
            <li><strong>RevenueCat:</strong> Subscription management</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Recognition & Milestones</h2>
          <ul>
            <li><strong>October 2025:</strong> GET AI PORTRAIT launches on iOS App Store</li>
            <li><strong>2024:</strong> Beta testing with 1000+ users across 15 countries</li>
            <li><strong>2024:</strong> GDPR & EU AI Act compliance certification</li>
            <li><strong>2024:</strong> Partnership with Replicate (FLUX 1.1 Pro integration)</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Our Commitment to Ethics</h2>
          <p>
            We take AI ethics seriously. GET AI PORTRAIT is built with the following principles:
          </p>
          <ul>
            <li><strong>No Deepfakes:</strong> Portraits are clearly labeled as AI-generated</li>
            <li><strong>No Facial Recognition:</strong> Face detection for artistic purposes only</li>
            <li><strong>Informed Consent:</strong> Users explicitly consent to biometric data processing</li>
            <li><strong>Data Minimization:</strong> We collect only what's necessary for portrait generation</li>
            <li><strong>Right to Deletion:</strong> Users can request full data deletion at any time</li>
            <li><strong>Bias Mitigation:</strong> AI trained on diverse datasets for fair representation</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Press & Media</h2>
          <p>
            For press inquiries, partnership opportunities, or media coverage, please visit our{' '}
            <a href="/press">Press Kit</a> or contact us directly.
          </p>
        </section>

        <section className="content-section">
          <h2>Contact Us</h2>
          <div className="contact-box">
            <p>
              <strong>GET AI PORTRAIT</strong><br />
              by DREIF AI
            </p>
            <p>
              Dominik Reif<br />
              Waldstraße 14<br />
              91091 Großenseebach<br />
              Germany
            </p>
            <p>
              General Inquiries: <a href="mailto:support@dreif-ai.com">support@dreif-ai.com</a><br />
              Press & Media: <a href="mailto:press@dreif-ai.com">press@dreif-ai.com</a><br />
              Website: <a href="https://getaiportrait.com" target="_blank" rel="noopener noreferrer">getaiportrait.com</a><br />
              Company: <a href="https://dreif-ai.com" target="_blank" rel="noopener noreferrer">dreif-ai.com</a>
            </p>
          </div>
        </section>

        <div style={{ marginTop: '40px', padding: '20px', background: '#f0f9ff', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
            Join thousands of users transforming their selfies into stunning AI portraits
          </p>
          <p style={{ margin: '10px 0 0', fontSize: '14px', color: '#666' }}>
            Available now on the iOS App Store
          </p>
        </div>
      </div>
    </div>
  );
};

export default About;